import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  Search,
  LogOut,
  Moon,
  Sun,
  User as UserIcon,
  Settings,
  Users,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import type { User } from '@/stores/authStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { Breadcrumbs } from './Breadcrumbs';
import { TimerGlobal } from './TimerGlobal';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui';

interface QHeaderProps {
  user: User | null;
  onMobileMenuToggle: () => void;
  onLogout: () => void;
  onOpenCommandPalette: () => void;
  notificationCount?: number;
  onlineAgentCount?: number;
  isWsConnected?: boolean;
}

function getInitials(nombre?: string): string {
  if (!nombre) return 'U';
  const parts = nombre.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function getRoleLabel(user: User | null): string {
  if (!user) return '';
  if (user.tipo === 'CLIENTE') return 'CLIENTE';
  // Pick the most relevant role from the roles array
  const roleMap: Record<string, string> = {
    admin: 'ADMIN',
    administrador: 'ADMIN',
    soporte: 'SOPORTE',
    desarrollo: 'DESARROLLO',
    gerente: 'GERENTE',
  };
  for (const role of user.roles) {
    const normalized = role.toLowerCase();
    if (roleMap[normalized]) return roleMap[normalized];
  }
  return 'SOPORTE';
}

export function QHeader({
  user,
  onMobileMenuToggle,
  onLogout,
  onOpenCommandPalette,
  notificationCount = 0,
  onlineAgentCount = 0,
  isWsConnected = false,
}: QHeaderProps) {
  const { preferencias, setTema } = usePreferencesStore();
  const activeEnv = useAuthStore((s) => s.activeEnv);
  const navigate = useNavigate();

  const isDark = preferencias.tema === 'dark' ||
    (preferencias.tema === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  const toggleDarkMode = () => {
    const next = isDark ? 'light' : 'dark';
    setTema(next);
    document.documentElement.classList.toggle('dark', next === 'dark');
  };

  const roleLabel = getRoleLabel(user);

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-background px-4">
      {/* Left: mobile hamburger + breadcrumbs */}
      <div className="flex items-center gap-3">
        <button
          aria-label="Abrir menú"
          onClick={onMobileMenuToggle}
          className="rounded-lg p-2 hover:bg-accent lg:hidden"
        >
          <Menu className="h-5 w-5 text-muted-foreground" />
        </button>
        <Breadcrumbs className="hidden sm:flex" />
      </div>

      {/* Right: actions */}
      <div className="flex items-center gap-1">
        {/* Search trigger — opens Cmd+K */}
        <button
          onClick={onOpenCommandPalette}
          className="hidden items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent sm:flex"
        >
          <Search className="h-4 w-4" />
          <span>Buscar...</span>
          <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>

        {/* Mobile search — icon only */}
        <Button
          aria-label="Buscar"
          variant="ghost"
          size="icon"
          onClick={onOpenCommandPalette}
          className="sm:hidden"
        >
          <Search className="h-5 w-5" />
        </Button>

        {/* Active timer indicator */}
        <TimerGlobal />

        {/* Online agents indicator */}
        {isWsConnected && onlineAgentCount > 0 && (
          <div className="hidden items-center gap-1.5 rounded-lg px-2 py-1 text-xs text-muted-foreground sm:flex">
            <Users className="h-3.5 w-3.5" />
            <span>{onlineAgentCount}</span>
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
          </div>
        )}

        {/* Dark mode toggle */}
        <Button
          aria-label={isDark ? 'Modo claro' : 'Modo oscuro'}
          variant="ghost"
          size="icon"
          onClick={toggleDarkMode}
        >
          {isDark ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>

        {/* Notifications */}
        <Button
          aria-label="Notificaciones"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/notificaciones')}
          className="relative"
        >
          <Bell className="h-5 w-5" />
          {notificationCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </Button>

        {/* Environment badge (solo si no es primary) */}
        {activeEnv && !activeEnv.is_primary && (
          <Badge
            variant="outline"
            className="hidden text-[10px] font-semibold md:inline-flex"
            style={{
              borderColor: activeEnv.color,
              color: activeEnv.color,
            }}
          >
            {activeEnv.label}
          </Badge>
        )}

        {/* Role badge */}
        {roleLabel && (
          <Badge
            variant="outline"
            className="hidden border-primary/30 text-[10px] text-primary md:inline-flex"
          >
            {roleLabel}
          </Badge>
        )}

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button aria-label="Menú de usuario" className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-accent">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-medium text-primary">
                  {getInitials(user?.nombre)}
                </span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="text-sm font-medium">{user?.nombre}</p>
              <p className="text-xs font-normal text-muted-foreground">
                {user?.email}
              </p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/perfil')}>
              <UserIcon className="mr-2 h-4 w-4" />
              Mi perfil
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/configuracion')}>
              <Settings className="mr-2 h-4 w-4" />
              Configuración
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
